import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DownloadExcelButton, DrilldownDialog, FullscreenModal, Loader, NewReactTable as TableComponent } from '../../ui/index.jsx';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Tooltip,
  Button,
  IconButton,
  ButtonGroup
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

import { AggregatedForecastPanelsSkeleton } from '../../common/ModulePageSkeleton/ModulePageSkeleton.jsx';
import { request, methods, isRequestCancelled } from '../../../../../utils/http-common.js';
import useCancellableRequest from '../../../../../hooks/useCancellableRequest.js';
import { formatNumberWithCommas } from '../../../../../helpers/numericHelpers.js';
import {
  isSensitiveFieldKey,
  renderSensitiveCellValue,
  renderSensitiveAutocompleteOption,
  sensitiveAutocompleteInputSx,
} from '../../common/sensitiveFields.jsx';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
const SEGMENT_ORDER = ['Royal', 'Elite', 'Aristocrat', 'Classic', 'Standard', 'New'];
const RISK_ORDER = ['P90', 'P70', 'P50'];

const SEGMENT_TITLES = {
  Royal: 'Royal Dealer',
  Elite: 'Elite Dealer',
  Aristocrat: 'Aristocrat Dealer',
  Classic: 'Classic Dealer',
  Standard: 'Standard Dealer',
  New: 'New Dealer',
};

const RISK_TITLES = {
  P90: 'P90 (Low Risk)',
  P70: 'P70 (Medium Risk)',
  P50: 'P50 (High Risk)',
};

const RATING_COLORS = {
  P90: { bg: '#e8f5e9', color: '#2e7d32' },
  P70: { bg: '#fff8e1', color: '#f57f17' },
  P50: { bg: '#fce4ec', color: '#c62828' },
};

const HEADER_BG = {
  p90_forecast: RATING_COLORS.P90.color,
  p70_forecast: RATING_COLORS.P70.color,
  p50_forecast: RATING_COLORS.P50.color,
};

const shimmerKeyframes = `
  @keyframes shimmer {
    0%   { background-position: 200% 0 }
    100% { background-position: -200% 0 }
  }
`;

const filterSelectSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 6, fontSize: 12 },
};

// Transform backend headers into TableComponent column definitions for aggregated tables
const buildColumns = (headers, onZoneRegionClick, groupKey, tableWidth = 720) => {
  const cols = headers || [];
  const colCount = Math.max(cols.length, 1);
  const hasLabel = cols.some((item) => item.key === 'label');
  const labelWidth = hasLabel ? Math.max(140, Math.round(tableWidth * 0.24)) : 0;
  const remainingWidth = Math.max(tableWidth - labelWidth, (hasLabel ? colCount - 1 : colCount) * 90);
  const otherCount = hasLabel ? colCount - 1 : colCount;
  const dataColWidth = otherCount > 0
    ? Math.max(90, Math.floor(remainingWidth / otherCount))
    : remainingWidth;

  return cols.map((item) => ({
    ...item,
    Header: () => (
      <span className={`${item.data_type === 'number' ? 'flex text-center jc-center' : ''}`}>
        {item.sub_label && item.sub_label !== '0' && item.sub_label !== '' ? (
          <span>
            {item.label} <br /> {item.sub_label}
          </span>
        ) : (
          <span>{item.label}</span>
        )}
      </span>
    ),
    width: item.key === 'label' ? labelWidth : dataColWidth,
    id: item.key,
    Cell: (cellProps) => {
      const rowData = cellProps.row.original;
      const val = rowData[item.key];
      const isNumber = item.data_type === 'number';
      const isTotal = rowData.is_total;
      const isVariance =
        item.key === 'mtd_variance' ||
        item.key === 'variance' ||
        item.key === 'mtd_variance_pct' ||
        item.key === 'variance_pct';

      // Clickable zone/region labels
      if (item.key === 'label' && !isTotal && rowData.row_type && onZoneRegionClick) {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              fontWeight: 400,
            }}
          >
            <span
              style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => onZoneRegionClick(rowData.label, groupKey, rowData.row_type)}
            >
              {rowData.label}
            </span>
          </div>
        );
      }

      let color = '#213547';
      if (isVariance && typeof val === 'number') {
        color = val > 0 ? '#2e7d32' : val < 0 ? '#c62828' : '#555';
      }

      let display = val ?? '—';
      if (isNumber && typeof val === 'number') {
        const rounded = Math.round(val * 100) / 100;
        display = item.key.endsWith('_pct')
          ? `${formatNumberWithCommas(rounded)}%`
          : formatNumberWithCommas(rounded);
      } else if (isSensitiveFieldKey(item.key)) {
        display = renderSensitiveCellValue(item.key, val, { empty: '—' });
      }

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isNumber ? 'center' : undefined,
            width: '100%',
            fontWeight: isTotal ? 700 : 400,
            color,
          }}
        >
          {display}
        </div>
      );
    },
  }));
};

// Build columns for the drilldown dealer table — matches parent ForecastReport buildColumns
const buildDrilldownColumns = (headers, columnSums, totalsLoading) =>
  headers.filter((h) => h.key !== 'forecast_rating').map((h) => {
    const isNumeric = h.data_type === 'number';

    const headerFn = () => (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2', gap: 2 }}>
        <span style={{ fontSize: '11px' }}>{h.label}</span>
        {isNumeric && (
          totalsLoading ? (
            <span style={{
              display: 'block',
              width: 48,
              height: 8,
              borderRadius: 3,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.2) 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }} />
          ) : columnSums[h.key] ? (
            <span style={{
              fontWeight: 600,
              fontSize: '10px',
              lineHeight: 1.2,
              color: '#ffffff',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              padding: '1px 6px',
              borderRadius: '8px',
            }}>
              {columnSums[h.key]}
            </span>
          ) : null
        )}
      </span>
    );

    const base = {
      key: h.key,
      id: h.key,
      label: h.label,
      Header: headerFn,
      data_type: h.data_type,
      sortable: h.sortable ?? false,
      filterable: h.filterable ?? false,
      width: h.key === 'dealer' ? 220 : h.key === 'upside_downside_vol' ? 160 : 110,
      ...(HEADER_BG[h.key] ? { headerStyle: { background: HEADER_BG[h.key] } } : {}),
    };

    if (h.key === 'upside_downside_vol') {
      return {
        ...base,
        Cell: ({ row }) => {
          const val = row.original.upside_downside_vol;
          if (val === '—' || val === undefined || val === null) {
            return <span style={{ color: '#aaa' }}>—</span>;
          }
          const raw = row.original.upside_downside_raw;
          const color = raw > 0 ? '#2e7d32' : raw < 0 ? '#c62828' : '#555';
          const prefix = raw > 0 ? '+' : '';
          return (
            <Tooltip title="Forecast vs avg of last 2 months" arrow>
              <span style={{ color, fontWeight: 600 }} className={typeof val === 'number'?"flex jc-center" : ""}>
                {prefix}{typeof val === 'number' ? formatNumberWithCommas(Math.round(val * 100) / 100) : val}
              </span>
            </Tooltip>
          );
        },
      };
    }

    if (isNumeric) {
      return {
        ...base,
        Cell: ({ row }) => {
          const val = row.original[h.key];
          if (val === null || val === undefined || val === '') {
            return <span style={{ color: '#aaa' }} className='flex jc-center'>—</span>;
          }
          return <span className='flex jc-center'>{typeof val === 'number' ? formatNumberWithCommas(Math.round(val * 100) / 100) : val}</span>;
        },
      };
    }

    return {
      ...base,
      Cell: ({ row }) => (
        <span>{renderSensitiveCellValue(h.key, row.original[h.key], { empty: '—' })}</span>
      ),
    };
  });

const AggregatedView = ({ viewType, month, handleDisableTab }) => {
  const { request: cancellableRequest } = useCancellableRequest(`sales-pnf/forecast-report/aggregated/${viewType}`);

  const [loading, setLoading] = useState(true);
  const [headers, setHeaders] = useState([]);
  const [groupData, setGroupData] = useState({});
  const [error, setError] = useState(null);
  const [exportingKey, setExportingKey] = useState(null);
  const [fullScreenKey, setFullScreenKey] = useState(null);

  // Account Owner filter state
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedFilter, setSelectedFilter] = useState({});
  const [loadingFilter, setLoadingFilter] = useState(false);

  // Drilldown state
  const [drilldown, setDrilldown] = useState(null); // { label, groupKey, filterKey }
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownHeaders, setDrilldownHeaders] = useState([]);
  const [drilldownRows, setDrilldownRows] = useState([]);
  const [drilldownColumnSums, setDrilldownColumnSums] = useState({});
  const [drilldownTotalsLoading, setDrilldownTotalsLoading] = useState(false);
  const [drilldownExporting, setDrilldownExporting] = useState(false);
  const [drilldownFetching, setDrilldownFetching] = useState(false);
  const [drilldownTotalRecords, setDrilldownTotalRecords] = useState(0);
  const ddPageRef = useRef(1);
  const ddFetchedRef = useRef(0);
  const ddSortRef = useRef([]);
  const ddFilterRef = useRef([]);
  const ddAdditionalFiltersRef = useRef({});

  const isAOView = viewType === 'account-owner' || viewType === 'account-owner-risk';
  const isRiskView = viewType === 'dealer-risk' || viewType === 'account-owner-risk';
  const isGYRRiskView = viewType === 'gyr-risk';
  const isProductCategoryView = viewType === 'product-category';

  const [currentIndex, setCurrentIndex] = useState(0);
  const ITEMS_PER_VIEW = 2;
  const panelsWrapperRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(() => (
    typeof window !== 'undefined'
      ? Math.max(520, Math.floor((window.innerWidth - 64) * 0.46))
      : 720
  ));

  // Derive masked filter values
  const maskedFilterRef = useRef({});
  useEffect(() => {
    const obj = {};
    Object.keys(selectedFilter).forEach((k) => {
      if (selectedFilter[k]) obj[k] = selectedFilter[k].value;
    });
    maskedFilterRef.current = obj;
  }, [selectedFilter]);

  // Fetch AO filter dropdown options
  const fetchFilters = async () => {
    setLoadingFilter(true);
    let fetchError;
    try {
      const payload = {
        zonal_head: selectedFilter.zonal_head ? selectedFilter.zonal_head.value : '',
        area_head: selectedFilter.area_head ? selectedFilter.area_head.value : '',
        district_officer: selectedFilter.district_officer ? selectedFilter.district_officer.value : '',
        account_owner: selectedFilter.account_owner ? selectedFilter.account_owner.value : '',
      };
      let res;
      [fetchError, res] = await cancellableRequest('salespnf/getfilterzonalhead', methods.POST, payload, { cancelKey: 'ao-filters' });
      if (isRequestCancelled(fetchError)) return;
      if (res?.data) {
        const opts = {};
        res.data.forEach((filter) => {
          if (filter.key === 'zonal_head') opts.zonal_head = filter.value;
          if (filter.key === 'area_head') opts.area_head = filter.value;
          if (filter.key === 'district_officer') opts.district_officer = filter.value;
          if (filter.key === 'ao') opts.ao = filter.value;
        });
        setFilterOptions(opts);
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch AO filters', e);
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setLoadingFilter(false);
      }
    }
  }

  useEffect(() => {
    if (isAOView) fetchFilters();
  }, [isAOView]);

  useEffect(() => {
    handleDisableTab(loading);
  }, [loading]);

  useEffect(() => {
    if (!isAOView) {
      setSelectedFilter((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      setFilterOptions((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }
  }, [isAOView]);

  // Reset to skeleton whenever tab view or month changes (e.g. MTD Chart -> Dealer Segment).
  useEffect(() => {
    setLoading(true);
    setError(null);
    setGroupData({});
    setHeaders([]);
    setCurrentIndex(0);
    setDrilldown(null);
  }, [viewType, month]);

  // Fetch aggregated data
  const fetchData = useCallback(async () => {
    if (!month) return;
    setLoading(true);
    setError(null);
    setDrilldown(null);
    setGroupData({});
    setHeaders([]);
    let fetchError;
    try {
      const payload = {
        month,
        view: viewType,
        ...(isAOView ? maskedFilterRef.current : {}),
      };
      let res;
      [fetchError, res] = await cancellableRequest('salespnf/getforecastreportaggregated', methods.POST, payload, { cancelKey: 'aggregated' });
      if (isRequestCancelled(fetchError)) return;
      if (res?.data) {
        setHeaders(res.data.row_header || []);
        setGroupData(res.data.row_data || {});
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch aggregated report', e);
        setError('Failed to load data');
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setLoading(false);
      }
    }
  }, [month, viewType, isAOView, selectedFilter, cancellableRequest]);

  useEffect(() => {
    setCurrentIndex(0)
    fetchData();
  }, [fetchData]);

  const handleExport = async (groupKey) => {
    if (!month || exportingKey) return;
    setExportingKey(groupKey);
    try {
      const payload = {
        month,
        view: viewType,
        export: 'true',
        ...(isProductCategoryView ? { material_classification: groupKey } : isGYRRiskView ? { district_classification: groupKey } : isRiskView
          ? { confidence_interval: groupKey }
          : { dealer_segment: groupKey }),
        ...(isAOView ? maskedFilterRef.current : {}),
      };
      const [, response] = await request(
        'salespnf/getforecastreportaggregated',
        methods.POST,
        payload,
        { responseType: 'blob' },
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      a.download =
        disposition.split('=')[1] ||
        `Forecast_Aggregated_${groupKey}_${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed for', groupKey, e);
    } finally {
      setExportingKey(null);
    }
  };

  // Build drilldown filters from drilldown state
  const buildDrilldownFilters = (dd) => {
    const filters = [
      { key: dd.filterKey, value: dd.label, data_type: 'string' },
    ];
    if (isRiskView) {
      filters.push({ key: 'forecast_rating', value: dd.groupKey, data_type: 'string' });
    } else if (isGYRRiskView) {
      filters.push({ key: 'district_classification', value: dd.groupKey, data_type: 'string' });
    } else if (isProductCategoryView) {
      // do nothing
    } else {
      filters.push({ key: 'dealer_segment', value: dd.groupKey, data_type: 'string' });
    }

    if (isAOView && selectedFilter.account_owner) {
      filters.push({ key: "account_owner", value: selectedFilter.account_owner.label, data_type: 'string' });
    }

    return filters;
  };

  const PAGE_SIZE = 50;

  // Fetch a page of drilldown rows
  const fetchDrilldownPage = useCallback(async (page, sortBy, filters, drilldownFilters, additionalFilters) => {
    setDrilldownFetching(true);
    let fetchError;
    try {
      const payload = {
        month,
        filters: [...drilldownFilters, ...(filters || [])],
        sort_by: sortBy || [],
        page_number: page,
        page_size: PAGE_SIZE,
        view: viewType,
        ...(additionalFilters || {}),
        ...(isAOView ? maskedFilterRef.current : {}),
      };
      let res;
      [fetchError, res] = await cancellableRequest('salespnf/getforecastreportv3', methods.POST, payload, { cancelKey: 'drilldown-data' });
      if (isRequestCancelled(fetchError)) return;
      if (res?.data) {
        const rowData = res.data.row_data || [];
        const total = res.data.total || 0;
        setDrilldownTotalRecords(total);
        if (page === 1) {
          setDrilldownHeaders(res.data.row_header || []);
          setDrilldownRows(rowData);
          ddFetchedRef.current = rowData.length;
        } else {
          setDrilldownRows((prev) => [...prev, ...rowData]);
          ddFetchedRef.current += rowData.length;
        }
        ddPageRef.current = page;
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Drilldown fetch failed', e);
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setDrilldownFetching(false);
      }
    }
  }, [month, isAOView, viewType, cancellableRequest]);

  // Fetch drilldown totals
  const fetchDrilldownTotals = useCallback(async (sortBy, filters, drilldownFilters, additionalFilters) => {
    setDrilldownTotalsLoading(true);
    let fetchError;
    try {
      const payload = {
        month,
        view: viewType,
        filters: [...drilldownFilters, ...(filters || [])],
        sort_by: sortBy || [],
        ...(isAOView ? maskedFilterRef.current : {}),
        ...(additionalFilters || {}),
      };
      let res;
      [fetchError, res] = await cancellableRequest('salespnf/getforecastreporttotalsv3', methods.POST, payload, { cancelKey: 'drilldown-totals' });
      if (isRequestCancelled(fetchError)) return;
      if (res?.data?.row_header) {
        const sums = {};
        res.data.row_header.forEach((h) => {
          if (h.sub_label && h.sub_label !== '' && h.sub_label !== '0') {
            sums[h.key] = formatNumberWithCommas(Math.round(parseFloat(h.sub_label) * 100) / 100);
          }
        });
        setDrilldownColumnSums(sums);
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Drilldown totals fetch failed', e);
      }
    } finally {
      if (!isRequestCancelled(fetchError)) {
        setDrilldownTotalsLoading(false);
      }
    }
  }, [month, isAOView, viewType, cancellableRequest]);

  // Drilldown: initial load when zone/region is clicked
  const handleZoneRegionClick = (label, groupKey, filterKey) => {
    const dd = { label, groupKey, filterKey };
    setDrilldown(dd);
    setDrilldownLoading(true);
    setDrilldownRows([]);
    setDrilldownColumnSums({});
    ddPageRef.current = 1;
    ddFetchedRef.current = 0;
    ddSortRef.current = [];
    ddFilterRef.current = [];
    ddAdditionalFiltersRef.current = {
      ...(isProductCategoryView ? { "material_classification": groupKey } : {}),
    };

    const drilldownFilters = buildDrilldownFilters(dd);
    // Fetch first page and totals in parallel
    Promise.all([
      fetchDrilldownPage(1, [], [], drilldownFilters, ddAdditionalFiltersRef.current),
      fetchDrilldownTotals([], [], drilldownFilters, ddAdditionalFiltersRef.current),
    ]).finally(() => setDrilldownLoading(false));
  };

  // Drilldown: scroll-to-load-more and sort/filter change
  const drilldownScrollFetch = useCallback(
    (scrollEvent, sortList, filterList) => {
      if (!drilldown) return;
      const drilldownFilters = buildDrilldownFilters(drilldown);
      if (scrollEvent) {
        const { scrollHeight, scrollTop, clientHeight } = scrollEvent;
        if (
          scrollHeight - scrollTop - clientHeight < 10 &&
          !drilldownFetching &&
          ddFetchedRef.current < drilldownTotalRecords
        ) {
          fetchDrilldownPage(ddPageRef.current + 1, ddSortRef.current, ddFilterRef.current, drilldownFilters, ddAdditionalFiltersRef.current);
        }
      } else {
        // Sort or filter changed — reset and refetch
        ddSortRef.current = sortList || [];
        ddFilterRef.current = filterList || [];
        ddPageRef.current = 1;
        ddFetchedRef.current = 0;
        // setDrilldownRows([]);
        // setDrilldownColumnSums({});
        fetchDrilldownPage(1, ddSortRef.current, ddFilterRef.current, drilldownFilters, ddAdditionalFiltersRef.current);
        fetchDrilldownTotals(ddSortRef.current, ddFilterRef.current, drilldownFilters, ddAdditionalFiltersRef.current);

      }
    },
    [drilldown, drilldownFetching, drilldownTotalRecords, fetchDrilldownPage, fetchDrilldownTotals],
  );

  // Drilldown: Excel export
  const handleDrilldownExport = async () => {
    if (drilldownExporting || !drilldown) return;
    setDrilldownExporting(true);
    try {
      const filters = buildDrilldownFilters(drilldown);
      const payload = {
        month,
        filters,
        export: 'true',
        ...(isAOView ? maskedFilterRef.current : {}),
        ...(ddAdditionalFiltersRef.current || {}),
      };
      const [, response] = await request('salespnf/getforecastreportv3', methods.POST, payload, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      a.download = disposition.split('=')[1] || `Forecast_${drilldown.label}_${drilldown.groupKey}_${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Drilldown export failed', e);
    } finally {
      setDrilldownExporting(false);
    }
  };

  // Get sorted group keys
  const getOrderedKeys = () => {
    const keys = Object.keys(groupData);
    if (keys.length === 0) return [];

    const order = isRiskView ? RISK_ORDER : SEGMENT_ORDER;
    const ordered = [];
    for (const key of order) {
      if (keys.includes(key)) ordered.push(key);
    }
    for (const key of keys.sort()) {
      if (!ordered.includes(key)) ordered.push(key);
    }
    return ordered;
  };

  const orderedKeys = getOrderedKeys();
  const titleMap = isRiskView ? RISK_TITLES : SEGMENT_TITLES;

  const visibleKeys = orderedKeys.slice(
    currentIndex,
    currentIndex + ITEMS_PER_VIEW
  );

  useEffect(() => {
    const el = panelsWrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const updateWidth = () => {
      const wrapperWidth = el.clientWidth;
      if (wrapperWidth <= 0) return;
      const nextPanelWidth = Math.max(520, Math.floor((wrapperWidth - 16) / ITEMS_PER_VIEW));
      setPanelWidth(nextPanelWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, orderedKeys.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      Math.max(prev - ITEMS_PER_VIEW, 0)
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      Math.min(prev + ITEMS_PER_VIEW, orderedKeys.length - ITEMS_PER_VIEW)
    );
  };

  const renderTable = (groupKey, isFullscreen = false) => {
    const rows = groupData[groupKey] || [];
    const title = titleMap[groupKey] || groupKey;
    const tableWidth = Math.max(
      520,
      (isFullscreen ? panelWidth * ITEMS_PER_VIEW + 16 : panelWidth) - 16,
    );
    const columns = buildColumns(headers, handleZoneRegionClick, groupKey, tableWidth);
    const tableMaxHeight = isFullscreen
      ? 'calc(80vh - 140px)'
      : 'calc(100vh - 470px)';

    return (
      <Box
        className="esf-table"
        key={isFullscreen ? undefined : groupKey}
        sx={{
          width: isFullscreen ? '100%' : 'calc(50% - 0.5em)',
          flex: isFullscreen ? undefined : '1 1 calc(50% - 0.5em)',
          minWidth: isFullscreen ? 0 : 520,
          ...(isFullscreen && { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }),
        }}
      >
        <Box
          height="100%"
          sx={{
            p: 1,
            backgroundColor: '#fff',
            borderRadius: 2,
            ...(isFullscreen && { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
          }}
        >
          {!isFullscreen && (
            <Box
              className="icon-excel flex"
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: 30,
                mb: 0.75,
              }}
            >
              <Typography className="sku-header" sx={{ alignSelf: 'flex-start !important' }}>
                {title}
              </Typography>
              {rows.length > 0 && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <DownloadExcelButton
                    onClick={() => handleExport(groupKey)}
                    loading={exportingKey === groupKey}
                    spinnerSize={16}
                  />
                  <Tooltip title="Fullscreen" arrow>
                    <IconButton size="small" onClick={() => setFullScreenKey(groupKey)} sx={{ p: 0.5 }}>
                      <FullscreenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
          <div
            className="table-region"
            style={isFullscreen ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
          >
            {rows.length > 0 ? (
              <div
                className="jklc-table-wrapper monthly-recon-table"
                style={isFullscreen ? { flex: 1, minHeight: 0 } : undefined}
              >
                <style>{`
                  .jklc-table-wrapper .table-container {
                    max-height: ${tableMaxHeight} !important;
                    min-height: ${isFullscreen ? tableMaxHeight : 'auto'};
                    overflow: auto !important;
                  }
                `}</style>
                <TableComponent
                  columns={columns}
                  data={rows}
                  tablePageSize={100}
                  hidePagination
                  className="hml-pure-table frame-4-table aggregated-panel-table"
                />
              </div>
            ) : (
              <div className="text-center mt-10">No data available for {title}</div>
            )}
          </div>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Account Owner filters */}
      {isAOView && (
        <Box display="flex" gap={1} mb={2} alignItems="center" flexWrap="wrap">
          {/* <Box width="15%" minWidth={160}>
            <Autocomplete
              size="small"
              fullWidth
              disabled={loadingFilter}
              options={filterOptions?.zonal_head || []}
              value={selectedFilter.zonal_head || null}
              onChange={(_, v) => setSelectedFilter({ ...selectedFilter, zonal_head: v })}
              getOptionLabel={(opt) => (opt ? opt.label : '')}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Zonal Head" variant="outlined" sx={filterSelectSx} />
              )}
            />
          </Box>
          <Box width="15%" minWidth={160}>
            <Autocomplete
              size="small"
              fullWidth
              disabled={loadingFilter}
              options={filterOptions?.area_head || []}
              value={selectedFilter.area_head || null}
              onChange={(_, v) => setSelectedFilter({ ...selectedFilter, area_head: v })}
              getOptionLabel={(opt) => (opt ? opt.label : '')}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Area Head" variant="outlined" sx={filterSelectSx} />
              )}
            />
          </Box>
          <Box width="15%" minWidth={160}>
            <Autocomplete
              size="small"
              fullWidth
              disabled={loadingFilter}
              options={filterOptions?.district_officer || []}
              value={selectedFilter.district_officer || null}
              onChange={(_, v) => setSelectedFilter({ ...selectedFilter, district_officer: v })}
              getOptionLabel={(opt) => (opt ? opt.label : '')}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select District Officer" variant="outlined" sx={filterSelectSx} />
              )}
            />
          </Box> */}
          <Box width="15%" minWidth={160}>
            <Autocomplete
              size="small"
              fullWidth
              disabled={loadingFilter}
              options={filterOptions?.ao || []}
              value={selectedFilter.account_owner || null}
              onChange={(_, v) => setSelectedFilter({ ...selectedFilter, account_owner: v })}
              getOptionLabel={(opt) => (opt ? opt.label : '')}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderOption={renderSensitiveAutocompleteOption}
              sx={{
                ...filterSelectSx,
                ...sensitiveAutocompleteInputSx(!!selectedFilter.account_owner),
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Account Owner" variant="outlined" sx={filterSelectSx} />
              )}
            />
          </Box>
          {/* <Tooltip title="Reset filters">
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => setSelectedFilter({})}
              sx={{ maxHeight: 30, minWidth: 0, p: 0.5 }}
            >
              <RefreshIcon fontSize="small" />
            </Button>
          </Tooltip> */}
          {loadingFilter && <Loader loading={loadingFilter}/>}
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Box display="flex" justifyContent="center" py={4}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Loading state — two panels side by side (Royal + Elite, or P90 + P70) */}
      {!error && loading && (
        <AggregatedForecastPanelsSkeleton variant={isRiskView ? 'risk' : 'segment'} />
      )}

      {/* Tables */}
      {!error && !loading && orderedKeys.length > 0 && (
        <Box position="relative">
          <Box
            ref={panelsWrapperRef}
            className="esf-table-wrapper flex"
            sx={{ display: 'flex', flexDirection: 'row', gap: '1em', alignItems: 'stretch', width: '100%', overflowX: 'auto' }}
          >
            {visibleKeys.map((key) => renderTable(key))}
          </Box>

          {/* Navigation Arrows */}
          <Box display="flex" justifyContent="center" mt={2}>
            <ButtonGroup aria-label="Next button group">
              <IconButton
                disabled={currentIndex === 0}
                onClick={handlePrev}
                sx={{
                  width: 35,
                  height: 35,
                  borderRadius: "50px",
                  background: "#D9D9D9",
                  "&:hover": { backgroundColor: "#c7c7c7" },
                }}
              >
                <NavigateBeforeIcon sx={{ fontSize: 30, color: "#4C515A" }} />
              </IconButton>
              <IconButton
                size="medium"
                onClick={handleNext}
                disabled={currentIndex + ITEMS_PER_VIEW >= orderedKeys.length}
                sx={{
                  width: 35,
                  height: 35,
                  borderRadius: "50px",
                  background: "#D9D9D9",
                  "&:hover": { backgroundColor: "#c7c7c7" },
                  marginLeft: "10px"
                }}
              >
                <NavigateNextIcon sx={{ fontSize: 30, color: "#4C515A" }} />
              </IconButton>
            </ButtonGroup>
          </Box>
        </Box>
      )}

      {/* Drilldown dealer modal */}
      <DrilldownDialog dragHandleSelector=".header"
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        dragResetKey={drilldown ? `forecast-agg-${drilldown.label}-${drilldown.groupKey}` : null}
        paperSx={{ width: '85vw', maxHeight: '80vh' }}
      >
        <Box
          className="drilldown-forecast-report-modal"
          sx={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <style>{shimmerKeyframes}</style>
          <Box
            className="header"
            sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2, py: 1.5,
            borderBottom: '1px solid #e0e0e0',
          }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#215093' }}>
              Forecast Report — {drilldown?.label} — {titleMap[drilldown?.groupKey] || drilldown?.groupKey}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <DownloadExcelButton
                onClick={handleDrilldownExport}
                loading={drilldownExporting}
                spinnerSize={16}
              />
              <IconButton size="small" onClick={() => setDrilldown(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {drilldownLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <Loader loading={drilldownLoading} />
              </Box>
            ) : drilldownRows.length > 0 ? (
              <div className="jklc-table-wrapper forecast-report-table">
                <TableComponent
                  columns={buildDrilldownColumns(drilldownHeaders, drilldownColumnSums, drilldownTotalsLoading)}
                  data={drilldownRows}
                  hidePagination
                  isFetching={drilldownFetching}
                  totalRecords={drilldownTotalRecords}
                  scrollFetch={drilldownScrollFetch}
                  stickyColumnCount={2}
                />
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No dealers found
              </Typography>
            )}
          </Box>
        </Box>
      </DrilldownDialog>

      <FullscreenModal
        open={!!fullScreenKey}
        onClose={() => setFullScreenKey(null)}
        headerTitle={fullScreenKey ? (titleMap[fullScreenKey] || fullScreenKey) : ''}
      >
        {fullScreenKey && (
          <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {renderTable(fullScreenKey, true)}
          </Box>
        )}
      </FullscreenModal>
    </Box>
  );
};

export default AggregatedView;
